import { SETTLED_DONATION_STATUSES } from '@impact/types/enums';
import type { StoredAdyenRedirectSession } from './adyenRedirectSession';

export interface RegistrationRedirectContext {
  status: string;
  sessionData: string | null;
  sessionId: string | null;
}

export interface RegistrationCompletionOptions {
  eventId?: string;
  registrationDonationId?: string;
  termsAccepted?: boolean;
  newsletterOptIn?: boolean;
  triumphMarketingOptIn?: boolean;
  movemberMarketingOptIn?: boolean;
}

export type RegistrationRedirectResolution =
  | {
      kind: 'complete-registration';
      completion: RegistrationCompletionOptions;
    }
  | {
      kind: 'finalize-payment';
      session: {
        donationId: string;
        sessionId: string;
        sessionData: string;
        redirectResult: string;
      };
    }
  | {
      kind: 'failed';
    };

export type RegistrationCompletionState = StoredAdyenRedirectSession['registrationCompletion'];

export function buildRegistrationCompletionState(params: {
  eventId: string;
  termsAccepted: boolean;
  newsletterOptIn: boolean;
  triumphMarketingOptIn: boolean;
  movemberMarketingOptIn: boolean;
}): NonNullable<RegistrationCompletionState> {
  return {
    eventId: params.eventId,
    termsAccepted: params.termsAccepted,
    newsletterOptIn: params.newsletterOptIn,
    triumphMarketingOptIn: params.triumphMarketingOptIn,
    movemberMarketingOptIn: params.movemberMarketingOptIn,
  };
}

export function buildRegistrationCompletionOptions(params: {
  eventId?: string;
  registrationDonationId?: string | null;
  serverCompletion?: RegistrationCompletionState | null;
  storedCompletion?: RegistrationCompletionState | null;
  fallbackCompletion?: RegistrationCompletionState | null;
}): RegistrationCompletionOptions {
  const completion =
    params.serverCompletion ?? params.storedCompletion ?? params.fallbackCompletion;

  return {
    eventId: completion?.eventId ?? params.eventId,
    registrationDonationId: params.registrationDonationId ?? undefined,
    termsAccepted: completion?.termsAccepted,
    newsletterOptIn: completion?.newsletterOptIn,
    triumphMarketingOptIn: completion?.triumphMarketingOptIn,
    movemberMarketingOptIn: completion?.movemberMarketingOptIn,
  };
}

export function resolveRegistrationRedirectFlow(params: {
  returnDonationId: string;
  redirectResult: string;
  returnEventId?: string | null;
  returnSessionId?: string | null;
  redirectContext: RegistrationRedirectContext;
  serverCompletion?: RegistrationCompletionState | null;
  storedSession?: Pick<StoredAdyenRedirectSession, 'sessionData' | 'registrationCompletion'> | null;
}): RegistrationRedirectResolution {
  const { redirectContext, redirectResult, returnDonationId, returnEventId, returnSessionId } =
    params;

  if ((SETTLED_DONATION_STATUSES as readonly string[]).includes(redirectContext.status)) {
    if (redirectContext.status === 'paid') {
      return {
        kind: 'complete-registration',
        completion: buildRegistrationCompletionOptions({
          eventId: returnEventId ?? undefined,
          registrationDonationId: returnDonationId,
          serverCompletion: params.serverCompletion,
          storedCompletion: params.storedSession?.registrationCompletion,
        }),
      };
    }

    return { kind: 'failed' };
  }

  const sessionData = params.storedSession?.sessionData || redirectContext.sessionData;
  const sessionId = returnSessionId || redirectContext.sessionId;

  if (!sessionId || !sessionData) {
    return { kind: 'failed' };
  }

  return {
    kind: 'finalize-payment',
    session: {
      donationId: returnDonationId,
      sessionId,
      sessionData,
      redirectResult,
    },
  };
}
